import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { EventSearchForm } from '@mark43/cad-api';
import { eventSearchFormDefaultValues } from '../configuration';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { EventSearchFormType } from '../types';
import { FrPage } from '../../fr-page-layout/fr-page';
import { EventSearchResults } from './event-search-results';
import { EventSearchFormBody } from '../event-search-form-body';
import { EventSearchFormFooter } from '../event-search-form-footer';
import { EventSearchSavedSearches } from '../event-search-saved-searches';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { PageHeader } from '../../fr-page-layout/search-pages/page-header';
import { FormWrapper } from '../../fr-page-layout/search-pages/form-wrapper';
import { useSearchState } from '../../fr-page-layout/search-pages/use-search-state';
import { useEventSearchBroadcaster } from '../use-event-search-broadcaster';
import { useEventSearchBootstrap } from '../use-event-search-bootstrap';
import { prepareForSubmit } from '../utils';
import { clearWindowHistoryState } from '../../common/helpers/utils';
import { useApplicationType } from '../../common/hooks/use-application-type';

export function EventSearch() {
    const {
        mutation,
        resultsOrSavedShown,
        showSavedSearches,
        runSavedSearch,
        closeSearches,
        openSavedSearch,
        searchData,
        savedSearchId,
        uniqueSearch,
        setSearchData,
        setSavedSearchId,
        showSearchResults,
        dateRangeOverride,
    } = useSearchState<EventSearchFormType, EventSearchForm>();
    const { isFirstResponder } = useApplicationType();

    useEventSearchBroadcaster(setSearchData, openSavedSearch);

    const location = useLocation();
    const { defaultFuzzyMatchingEnabled, defaultIncludeActiveEnabled } = useEnabledFeatures();

    const defaultValues = useMemo(
        () => ({
            ...eventSearchFormDefaultValues,
            fuzzyMatchingEnabled: defaultFuzzyMatchingEnabled,
            includeActiveEvents: defaultIncludeActiveEnabled,
        }),
        [defaultFuzzyMatchingEnabled, defaultIncludeActiveEnabled]
    );

    const form = useForm<EventSearchFormType>({
        mode: 'onBlur',
        defaultValues,
    });

    const query = useEventSearchBootstrap();

    const eventSearchBootstrapResponse = query.data;

    const fuzzyMatchingEnabled = useWatch({
        control: form.control,
        name: 'fuzzyMatchingEnabled',
    });

    useEffect(
        function setSavedEventSearchData() {
            const savedSearchData: EventSearchForm | undefined = location.state?.eventSearchResults;

            if (savedSearchData) {
                setSearchData({ search: savedSearchData });
            }
        },
        [location.state?.eventSearchResults, setSearchData]
    );

    const onSubmit = useCallback(
        (eventSearchForm: EventSearchFormType): void => {
            // @ts-expect-error
            mutation.mutate(prepareForSubmit(eventSearchForm));
        },
        [mutation]
    );

    const closeSearchesAndClearLocationState = () => {
        closeSearches();
        clearWindowHistoryState();
    };

    return (
        <FrPage>
            <PageHeader
                text="Event Search"
                resultsOrSavedShown={resultsOrSavedShown}
                openSavedSearch={openSavedSearch}
                showSavedSearches={showSavedSearches}
                closeSearches={closeSearchesAndClearLocationState}
            />

            <IntegratedFormProvider
                form={form}
                query={query}
                mutation={mutation}
                fuzzyMatchingEnabled={fuzzyMatchingEnabled}
                onSubmit={onSubmit}
            >
                <FormWrapper
                    query={query}
                    form={form}
                    resultsOrSavedShown={resultsOrSavedShown}
                    formBody={
                        <EventSearchFormBody
                            isFirstResponder={isFirstResponder}
                            form={form}
                            options={eventSearchBootstrapResponse.eventSearchOptions}
                            statics={eventSearchBootstrapResponse.eventSearchStatics}
                        />
                    }
                    footer={<EventSearchFormFooter form={form} />}
                />
                {showSavedSearches && (
                    <EventSearchSavedSearches onSavedSearchClick={runSavedSearch} />
                )}
            </IntegratedFormProvider>
            {showSearchResults && searchData && (
                <EventSearchResults
                    savedSearchId={savedSearchId}
                    searchData={searchData}
                    closeSearch={closeSearches}
                    searchTab={uniqueSearch}
                    setSavedSearchId={setSavedSearchId}
                    dateRangeOverride={dateRangeOverride}
                />
            )}
        </FrPage>
    );
}
